import { Button, FileUploadIcon, FolderIcon, IconButton, ImageAddIcon, TextInput, TrashCanIcon } from "@datagame/ui";
import { useDropzone } from "react-dropzone";
import { useEffect, useState } from "react";
import { useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";

import { CREATE_LIBRARY_IMAGE } from "../../../../../../../graphql/mutations/LibraryImageMutations";
import { LibraryImage, LibraryImageKind } from "../../../../../../../gql/graphql";
import { LibraryModal } from "../../../../../../shared/modals/LibraryModal";

import styles from "./ImageInput.module.scss";

export interface ImageInputProps {
  image?: LibraryImage | null;
  onUpload: (newImage: LibraryImage) => void;
  kind: LibraryImageKind;
  showRemoveButton?: boolean;
  onRemove?: () => void;
}

export const ImageInput = ({ image, onUpload, kind, showRemoveButton, onRemove }: ImageInputProps) => {
  const { t } = useTranslation();
  const [isLibraryModalOpen, setIsLibraryModalOpen] = useState(false);
  const [imageName, setImageName] = useState(image?.name || "");
  const [uploadImage] = useMutation(CREATE_LIBRARY_IMAGE);

  const onFileUpload = (files: File[]) => {
    uploadImage({
      variables: {
        input: {
          files,
          kind,
        },
      },
      onCompleted: (data) => {
        onUpload(data.libraryImageCreate[0] as LibraryImage);
      },
    });
  };
  const { getRootProps, getInputProps } = useDropzone({
    onDrop: onFileUpload,
    accept: {
      "image/jpeg": [],
      "image/png": [],
    },
    maxFiles: 1,
  });

  useEffect(() => {
    setImageName(image?.name || "");
  }, [image]);

  const selectImage = (image: LibraryImage) => {
    setIsLibraryModalOpen(false);
    onUpload(image);
  };

  const displayRemoveButton = showRemoveButton && image;

  return (
    <>
      <div className={styles.imageSection}>
        <div className={styles.inputWrapper}>
          <TextInput size="small" icon={<FileUploadIcon />} readOnly value={imageName} />
          {displayRemoveButton && (
            <IconButton onClick={onRemove} className={styles.deleteButton} icon={<TrashCanIcon />} />
          )}
        </div>
        <div className={styles.uploadButtons}>
          <div {...getRootProps()} className={styles.button}>
            <input {...getInputProps()} />
            <Button kind="outline" className={styles.button} icon={<ImageAddIcon />} iconSide="left">
              {t("project:advancedSettings.form.upload")}
            </Button>
          </div>

          <Button
            kind="outline"
            className={styles.button}
            icon={<FolderIcon />}
            iconSide="left"
            onClick={() => setIsLibraryModalOpen(true)}
          >
            {t("project:advancedSettings.form.library")}
          </Button>
        </div>
      </div>

      {isLibraryModalOpen && (
        <LibraryModal onSelect={selectImage} kind={kind} onClose={() => setIsLibraryModalOpen(false)} />
      )}
    </>
  );
};
